import type { FrameRecords } from "../types/api";
import { fmtCell, type VisibleColumn } from "./columns";

// The Manual Match ledgers' column order, row order and track chips (finance 2026-09-27),
// used by every other grid since 2026-09-28 (sort on request, chips, and column order on
// the single-book grids bar the Statement tab).
// Pure functions, ported to tests/ledger_view_port.py — keep the two in lockstep.

// Hidden from a ledger WHEN every row on screen holds the same value ("In CRI, not
// Insurer", "In period"), since they then tell someone matching nothing. Period is not
// always constant: 13 books across 12 insurers carry open rows before or after the period
// (all-insurer sweep 2026-09-27), and there it stays. The statement tabs and the workbook
// always show both.
export const LEDGER_HIDDEN = new Set(["match_status", "period_position"]);

// What a row is matched on, in the order it leads both books: date, amount, reference,
// policy, party, details. A role takes the FIRST of its canonical keys (the names in
// reporting/filters.py::_get_display_config) that the grid shows, so each book keeps its
// own column names; a role with no such column is skipped. Amount is the column the
// engine's manual-match gate sums (cri_amount / balance_amount).
const KEY_ROLES: readonly (readonly string[])[] = [
  ["doc_date"],
  ["cri_amount", "balance_amount"],
  ["reference_no", "document_number", "reference", "transaction_code", "tran_code", "doc_reference"],
  ["policy_no", "pol_endt_clm_no"],
  ["customer_name", "client_name", "assured_name"],
  ["description", "narration"],
];

const BLANKS = new Set(["", "nan", "NaT", "None"]);

function isBlank(v: unknown): boolean {
  return v === null || v === undefined || BLANKS.has(String(v).trim());
}

// Two cells hold the same data: the same value, both blank, or the same text as stored or
// as displayed (a raw "Doc Date" column may carry a time the canonical doc_date does not).
function sameCell(a: unknown, ka: string, b: unknown, kb: string): boolean {
  if (a === b) return true;
  const ba = isBlank(a), bb = isBlank(b);
  if (ba || bb) return ba && bb;
  if (String(a).trim() === String(b).trim()) return true;
  return fmtCell(a, ka) === fmtCell(b, kb);
}

const REPEAT_CACHE = new WeakMap<FrameRecords, Map<string, Set<string>>>();

/** Keys of the columns in `cols` whose every row repeats an EARLIER column's data.
 *
 *  Checked over the whole book (`frame`), not the unmatched slice on screen, so a column
 *  cannot jump to the end mid-session because the rows that told it apart were matched
 *  away. Each pair early-exits on its first differing row, so only true repeats pay a full
 *  scan; cached per frame, which a re-finalize replaces. */
export function repeatedColumns(frame: FrameRecords, cols: VisibleColumn[]): Set<string> {
  const sig = cols.map((c) => c.key).join("\u0001");
  let byCols = REPEAT_CACHE.get(frame);
  if (!byCols) { byCols = new Map(); REPEAT_CACHE.set(frame, byCols); }
  const hit = byCols.get(sig);
  if (hit) return hit;
  const idx = cols.map((c) => frame.columns.indexOf(c.key));
  const rows = frame.rows;
  const kept: number[] = [];
  const out = new Set<string>();
  for (let j = 0; j < cols.length; j++) {
    const ij = idx[j];
    const repeat = ij >= 0 && rows.length > 0 && kept.some((i) => {
      const ii = idx[i];
      if (ii < 0) return false;
      for (let p = 0; p < rows.length; p++) {
        if (!sameCell(rows[p][ij], cols[j].key, rows[p][ii], cols[i].key)) return false;
      }
      return true;
    });
    if (repeat) out.add(cols[j].key); else kept.push(j);
  }
  byCols.set(sig, out);
  return out;
}

function constantIn(rows: FrameRecords["rows"], index: number): boolean {
  if (!rows.length) return true;
  const first = String(rows[0][index]);
  for (let p = 1; p < rows.length; p++) if (String(rows[p][index]) !== first) return false;
  return true;
}

/** The ledger column order: the matching fields first, then every other column as today,
 *  then the columns that repeat another column's data (judged over `frame`, the whole
 *  book). A status column is hidden when constant over `rows`, the rows the grid shows.
 *  Pins are applied on top by the caller, so a pinned column still leads. */
export function ledgerColumns(cols: VisibleColumn[], frame: FrameRecords,
                              rows: FrameRecords["rows"]): VisibleColumn[] {
  const shown = cols.filter((c) => !LEDGER_HIDDEN.has(c.key) || !constantIn(rows, c.index));
  const lead: VisibleColumn[] = [];
  for (const role of KEY_ROLES) {
    for (const k of role) {
      const c = shown.find((x) => x.key === k);
      if (c) { lead.push(c); break; }
    }
  }
  const ordered = [...lead, ...shown.filter((c) => !lead.includes(c))];
  const rep = repeatedColumns(frame, ordered);
  if (!rep.size) return ordered;
  return [...ordered.filter((c) => !rep.has(c.key)), ...ordered.filter((c) => rep.has(c.key))];
}

// ---- Sorting -------------------------------------------------------------------------

export type SortKind = "date" | "number" | "text";
export type SortDir = "asc" | "desc";
export interface SortSpec { key: string; dir: SortDir }

// Worded per kind, the way Excel's filter button words them.
export const SORT_LABELS: Record<SortKind, Record<SortDir, string>> = {
  date: { desc: "Newest to oldest", asc: "Oldest to newest" },
  number: { desc: "Largest to smallest", asc: "Smallest to largest" },
  text: { asc: "A to Z", desc: "Z to A" },
};

const KIND_CACHE = new WeakMap<FrameRecords, Map<number, SortKind>>();

/** A column's kind: "date" by name (the rule fmtCell and the date filter use), "number"
 *  when every filled cell holds a number, otherwise "text". Cached per frame. */
export function sortKindOf(frame: FrameRecords, index: number, key: string): SortKind {
  if (key.toLowerCase().includes("date")) return "date";
  let m = KIND_CACHE.get(frame);
  if (!m) { m = new Map(); KIND_CACHE.set(frame, m); }
  const hit = m.get(index);
  if (hit) return hit;
  let filled = 0;
  let kind: SortKind = "number";
  for (const r of frame.rows) {
    const v = r[index];
    if (isBlank(v)) continue;
    filled++;
    if (typeof v !== "number") { kind = "text"; break; }
  }
  if (!filled) kind = "text";
  m.set(index, kind);
  return kind;
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}/;
const COLLATOR = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

function sortValue(v: unknown, kind: SortKind): string | number | null {
  if (isBlank(v)) return null;
  if (kind === "number") return typeof v === "number" && Number.isFinite(v) ? v : null;
  const s = String(v).trim();
  if (kind === "date") {
    const m = ISO_DAY.exec(s);
    return m ? m[0] : null;
  }
  return s;
}

/** Positions of `rows` in sorted order. Blank (or, in a date column, undated) cells go
 *  last in BOTH directions; ties keep their current order. */
export function sortOrder(rows: FrameRecords["rows"], index: number, kind: SortKind,
                          dir: SortDir): number[] {
  const keys = rows.map((r) => sortValue(r[index], kind));
  const sign = dir === "desc" ? -1 : 1;
  const pos = rows.map((_, p) => p);
  pos.sort((a, b) => {
    const ka = keys[a], kb = keys[b];
    if (ka === null || kb === null) {
      if (ka === kb) return a - b;
      return ka === null ? 1 : -1;
    }
    let c: number;
    if (kind === "number") c = (ka as number) - (kb as number);
    else if (kind === "date") c = (ka as string) < (kb as string) ? -1 : (ka as string) > (kb as string) ? 1 : 0;
    else c = COLLATOR.compare(ka as string, kb as string);
    return c !== 0 ? sign * c : a - b;
  });
  return pos;
}

/** Positions of `rows` in `sort`'s order, or null to keep theirs (no sort, or its column
 *  is not in `frame`). The kind is judged over the whole `frame`, as the header shows it. */
export function sortPositions(frame: FrameRecords, rows: FrameRecords["rows"],
                              sort: SortSpec | null): number[] | null {
  const ci = sort ? frame.columns.indexOf(sort.key) : -1;
  if (!sort || ci < 0) return null;
  return sortOrder(rows, ci, sortKindOf(frame, ci, sort.key), sort.dir);
}

// ---- Track chips ---------------------------------------------------------------------

const TRACK_ORDER = ["premium", "commission", "claims"];

/** The distinct tracks in a book: premium, commission, claims first, anything else after. */
// "Find this amount": one document sits in the two books with opposite signs, so a cell
// holds the amount when its magnitude equals it to the fils.
export function holdsAmount(v: unknown, target: number): boolean {
  return typeof v === "number" && Math.abs(Math.abs(v) - target) < 0.005;
}

/** Rows of `frame` whose `column` holds `target` (holdsAmount); 0 when the column is absent. */
export function countAmount(frame: FrameRecords, column: string, target: number): number {
  const i = frame.columns.indexOf(column);
  if (i < 0) return 0;
  let n = 0;
  for (const r of frame.rows) if (holdsAmount(r[i], target)) n++;
  return n;
}

// "Find this policy" (finance 2026-09-28, measured on every golden month): a row of the
// other book shows when it shares one of the engine's policy keys with the ticked rows,
// OR any of its columns contains their full policy. The keys alone missed the real
// counterpart 40-60% of the time on Hayah/Orient/Dubai/Sukoon/Watania, whose insurer
// keeps the policy in the narration. `_pol` is the full policy then its keys,
// "\x01"-joined, shipped on open rows by api/serialize.py.
export interface PolicyTarget { full: string; keys: string[] }

// The engine's floor for a derived policy key (match_keys._MIN_SCOPED_KEY_LEN): shorter
// text would sit inside unrelated cells.
const MIN_POLICY_TEXT = 4;

function policyCell(v: unknown): PolicyTarget | null {
  const s = typeof v === "string" ? v : "";
  if (!s) return null;
  const [full, ...keys] = s.split("\x01");
  return { full, keys: full ? [full, ...keys] : keys };
}

/** The ticked rows' policy, or null unless every one has the same non-blank policy. */
export function policyTarget(frame: FrameRecords, positions: number[]): PolicyTarget | null {
  const pi = frame.columns.indexOf("_pol");
  if (pi < 0 || !positions.length) return null;
  let full = "";
  const keys = new Set<string>();
  for (const p of positions) {
    const c = policyCell(frame.rows[p]?.[pi]);
    if (!c || !c.full || (full && c.full !== full)) return null;
    full = c.full;
    for (const k of c.keys) keys.add(k);
  }
  return { full, keys: [...keys] };
}

const ROW_TEXT_CACHE = new WeakMap<FrameRecords, string[]>();

// Every shown column of each row, upper-cased and joined, built once per frame.
function rowTexts(frame: FrameRecords): string[] {
  let t = ROW_TEXT_CACHE.get(frame);
  if (!t) {
    const cols = frame.columns.map((c, i) => (c.startsWith("_") ? -1 : i)).filter((i) => i >= 0);
    t = frame.rows.map((r) => cols.map((i) => {
      const v = r[i];
      return v === null || v === undefined ? "" : String(v).toUpperCase();
    }).join("\u0001"));
    ROW_TEXT_CACHE.set(frame, t);
  }
  return t;
}

/** A predicate over row positions of `frame`: does the row carry `target`'s policy? */
export function policyMatcher(frame: FrameRecords, target: PolicyTarget): (pos: number) => boolean {
  const pi = frame.columns.indexOf("_pol");
  const want = new Set(target.keys);
  const needle = target.full.length >= MIN_POLICY_TEXT ? target.full : "";
  const texts = needle ? rowTexts(frame) : [];
  return (pos) => {
    const c = pi >= 0 ? policyCell(frame.rows[pos][pi]) : null;
    if (c && c.keys.some((k) => want.has(k))) return true;
    return needle !== "" && texts[pos].includes(needle);
  };
}

/** Rows of `frame` carrying `target`'s policy (policyMatcher). */
export function countPolicy(frame: FrameRecords, target: PolicyTarget): number {
  const m = policyMatcher(frame, target);
  let n = 0;
  for (let p = 0; p < frame.rows.length; p++) if (m(p)) n++;
  return n;
}

export function trackValues(frame: FrameRecords): string[] {
  const i = frame.columns.indexOf("track");
  if (i < 0) return [];
  const seen = new Set<string>();
  for (const r of frame.rows) {
    const v = r[i];
    if (!isBlank(v)) seen.add(String(v));
  }
  const known = TRACK_ORDER.filter((t) => seen.has(t));
  const rest = [...seen].filter((t) => !TRACK_ORDER.includes(t)).sort();
  return [...known, ...rest];
}

export function trackLabel(track: string): string {
  return track.charAt(0).toUpperCase() + track.slice(1);
}
